<script setup lang="ts">
import type { InfoNoteElementProps } from "./InfoNoteElementProps";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import ScrollAnimation from "@/views/blocks/shared/section/animations/ScrollAnimation.vue";
import { computed } from "vue";
import HorizontalSpacing from "@/views/blocks/shared/section/layout/spacing/HorizontalSpacing.vue";

const props = defineProps<{
    block: PageBlock<InfoNoteElementProps>;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <ScrollAnimation :type="scrollAnimationType">
        <div
            :id="block.webId"
            :style="{
                backgroundColor: styleValues.background,
            }"
        >
            <HorizontalSpacing
                :class="[
                    dataValues.formatting.data.paddingTop,
                    dataValues.formatting.data.paddingBottom,
                ]"
            >
                <div class="mx-auto max-w-2xl lg:max-w-7xl">
                    <EditableElement
                        :blockId="block.id"
                        field-name="infoNote"
                        v-if="!dataValues.infoNote.isHidden"
                    >
                        <div
                            class="flex items-center gap-2"
                            :class="[
                                dataValues.formatting.data.elementAlign,
                                dataValues.formatting.data.maxWidth,
                                dataValues.formatting.data.textSize,
                                dataValues.formatting.data.justifyContent,
                            ]"
                            :style="{
                                color: styleValues.text,
                            }"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                                class="w-5 h-5 flex-none"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <Paragraph
                                :text="dataValues.infoNote.data.text"
                                :text-color="styleValues.text"
                                class="opacity-70"
                            />
                        </div>
                    </EditableElement>
                </div>
            </HorizontalSpacing>
        </div>
    </ScrollAnimation>
</template>
